import { Fragment, useState, useEffect, useRef } from 'react';
import type { FormEvent, MouseEvent } from 'react';
import type { AquariumFormData, Aquarium } from '../types/aquarium';
import { useTranslation } from '../i18n/useTranslation';
import { usePhotoUpload } from '../hooks/usePhotoUpload';
import { PhotoUploadDropzone } from './atoms/PhotoUploadDropzone/PhotoUploadDropzone';
import { useAquariumFormTabs } from './AquariumForm/useAquariumFormTabs';
import { AquariumFormTabs } from './AquariumForm/AquariumFormTabs';
import { AquariumFormTabPanel } from './AquariumForm/AquariumFormTabPanel';
import { TAB_IDS, TAB_FIELDS } from './AquariumForm/useAquariumFormTabs';
import type { TabId } from './AquariumForm/useAquariumFormTabs';
import { classifySubmitError } from './AquariumForm/aquariumFormErrors';
import type { AquariumFieldName } from './AquariumForm/aquariumFormErrors';
import { Heading } from './atoms/Typography/Heading';
import { Button } from './atoms/Button/Button';
import { Input } from './atoms/Input/Input';
import { Select } from './atoms/Select/Select';
import { Text } from './atoms/Typography/Text';
import { Textarea } from './atoms/Textarea/Textarea';
import './AquariumForm.css';
import { Alert } from './atoms/Alert/Alert';
import { FieldError } from './atoms/FieldError/FieldError';

interface AquariumFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AquariumFormData) => Promise<string | void>;
  aquarium?: Aquarium | null;
  isLoading?: boolean;
  /**
   * Submit failure. A string is shown as-is (already translated by the
   * caller); an error object (e.g. ApiError) is mapped to a translated
   * message — the raw backend text is never shown.
   */
  error?: unknown;
}

type FormErrors = Partial<Record<AquariumFieldName, string>>;

/** Numeric fields: optional, but when filled must be a number > 0 (backend: positive). */
const NUMERIC_FIELDS = ['grossVolume', 'netVolume', 'length', 'width', 'height'] as const;
type NumericField = (typeof NUMERIC_FIELDS)[number];

/** DOM id of each validated field's control (focus target for "go to field"). */
const FIELD_INPUT_IDS: Partial<Record<AquariumFieldName, string>> = {
  name: 'aq-name',
  description: 'aq-description',
  tankType: 'aq-tank-type',
  setupDate: 'aq-setup-date',
  grossVolume: 'aq-gross-volume',
  netVolume: 'aq-net-volume',
  length: 'aq-length',
  width: 'aq-width',
  height: 'aq-height',
  sumpAvailable: 'aq-has-sump-yes',
  supplySystem: 'aq-supply-system',
  equipmentNotes: 'aq-equipment-notes',
  notes: 'aq-notes',
};

function tabOfField(field: AquariumFieldName): TabId {
  return TAB_IDS.find((id) => TAB_FIELDS[id].includes(field)) ?? 'basic';
}

// Internal form state per equipment type
interface EquipmentEntry {
  type: EquipmentType;
  enabled: boolean;
  units: Array<{ brand: string; model: string; installedAt: string }>;
}

const TANK_TYPE_SUGGESTIONS = [
  'Reef Tank',
  'Mixed Reef',
  'SPS Dominant',
  'LPS Dominant',
  'Fish Only',
  'Fish Only with Live Rock',
  'Freshwater',
  'Planted Freshwater',
  'Brackish',
];

// Labels come from i18n: aquarium.form.supplySystem.options.<value>
const SUPPLY_SYSTEMS = [
  'standard',
  'zeovit',
  'balling',
  'balling_light',
  'np_biopellets',
  'triton',
  'ati_essentials',
  'custom',
] as const;

// Labels come from i18n: aquarium.form.equipmentTypes.<type>
const EQUIPMENT_TYPES = [
  'skimmer',
  'return_pump',
  'flow_pump',
  'lights',
  'media_reactor',
  'calcium_reactor',
  'zeolite_reactor',
  'uv_sterilizer',
  'ozone_generator',
  'gfo_reactor',
  'refugium',
  'dosing_pump',
  'ato',
  'vliessfilter',
  'chiller',
  'heater',
] as const;
type EquipmentType = (typeof EQUIPMENT_TYPES)[number];

const DEFAULT_UNIT = { brand: '', model: '', installedAt: '' };

function makeDefaultEquipment(): EquipmentEntry[] {
  return EQUIPMENT_TYPES.map((type) => ({
    type,
    enabled: false,
    units: [{ ...DEFAULT_UNIT }],
  }));
}

export function AquariumForm({
  isOpen,
  onClose,
  onSubmit,
  aquarium,
  isLoading = false,
  error = null,
}: AquariumFormProps) {
  const { t } = useTranslation();
  // Install dates (#1828) cannot lie in the future — the user's local date, not UTC.
  const now = new Date();
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const isEditMode = aquarium != null;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tankType, setTankType] = useState('');
  const [setupDate, setSetupDate] = useState('');
  const [grossVolume, setGrossVolume] = useState('');
  const [netVolume, setNetVolume] = useState('');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  // Always answered: defaults to "no" so the payload never carries null.
  const [sumpAvailable, setSumpAvailable] = useState(false);
  const [supplySystem, setSupplySystem] = useState<string>('');
  const [equipment, setEquipment] = useState<EquipmentEntry[]>(makeDefaultEquipment);
  const [equipmentNotes, setEquipmentNotes] = useState('');
  const [notes, setNotes] = useState('');
  const [touched, setTouched] = useState<ReadonlySet<AquariumFieldName>>(new Set());
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The submit-error banner is hidden as soon as the user edits the form again.
  const [submitErrorDismissed, setSubmitErrorDismissed] = useState(false);
  // Field to focus once its tab panel is visible ("go to field").
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);

  // Tab state
  const { activeTab, setActiveTab, tabHasError } = useAquariumFormTabs('basic');

  // ── Validation (live) ─────────────────────────────────────────────────────
  // Only the name is required; everything else is optional, but numbers that
  // are filled in must be > 0 (the backend rejects 0 / negative values).
  const numericValues: Record<NumericField, string> = { grossVolume, netVolume, length, width, height };
  const trimmedName = name.trim();
  const clientErrors: FormErrors = {};
  if (!trimmedName) {
    clientErrors.name = t('aquarium.form.name.required');
  } else if (trimmedName.length > 100) {
    clientErrors.name = t('aquarium.form.name.tooLong');
  }
  for (const field of NUMERIC_FIELDS) {
    const raw = numericValues[field];
    if (raw !== '') {
      const value = Number(raw);
      if (!Number.isFinite(value) || value <= 0) {
        clientErrors[field] = t('aquarium.form.positiveNumber');
      }
    }
  }
  const missingRequired: AquariumFieldName[] = trimmedName ? [] : ['name'];
  const invalidFields = (Object.keys(clientErrors) as AquariumFieldName[]).filter(
    (f) => !missingRequired.includes(f),
  );
  const problemFields: AquariumFieldName[] = [...missingRequired, ...invalidFields];
  const isValid = problemFields.length === 0;

  // ── Submit (server) error ─────────────────────────────────────────────────
  const submitError = submitErrorDismissed ? null : classifySubmitError(error);
  const serverFieldErrors: FormErrors =
    submitError?.kind === 'validation' && submitError.field
      ? { [submitError.field]: t('aquarium.form.invalidField') }
      : {};
  let bannerMessage: string | null = null;
  if (submitError?.kind === 'message') bannerMessage = submitError.message;
  else if (submitError?.kind === 'validation') bannerMessage = t('aquarium.form.validationError');
  else if (submitError?.kind === 'failed') bannerMessage = t('aquarium.form.saveFailed');

  /** Error shown at a field: client errors once touched / after a submit attempt, else server errors. */
  function fieldError(field: AquariumFieldName): string | undefined {
    if (clientErrors[field] && (touched.has(field) || submitAttempted)) return clientErrors[field];
    return serverFieldErrors[field];
  }

  function markTouched(field: AquariumFieldName): void {
    setTouched((prev) => (prev.has(field) ? prev : new Set(prev).add(field)));
  }

  function fieldLabel(field: AquariumFieldName): string {
    switch (field) {
      case 'name': return t('aquarium.form.name.label');
      case 'grossVolume': return `${t('aquarium.form.volume.label')} – ${t('aquarium.form.grossVolume')}`;
      case 'netVolume': return `${t('aquarium.form.volume.label')} – ${t('aquarium.form.netVolume')}`;
      case 'length': return t('aquarium.form.length');
      case 'width': return t('aquarium.form.width');
      case 'height': return t('aquarium.form.height');
      default: return field;
    }
  }

  // A tab is flagged when one of its fields shows an error, or when it holds a
  // problem the user cannot see because another tab is active.
  const displayedErrorFields = [
    ...(Object.keys(clientErrors) as AquariumFieldName[]).filter((f) => fieldError(f) != null),
    ...(Object.keys(serverFieldErrors) as AquariumFieldName[]),
  ];
  const errorTabs = new Set<TabId>(
    TAB_IDS.filter(
      (id) =>
        tabHasError(id, displayedErrorFields) ||
        (id !== activeTab && tabHasError(id, problemFields)),
    ),
  );

  /** Switch to the tab holding `field` and move focus to its control. */
  function goToField(field: AquariumFieldName): void {
    setPendingFocusId(FIELD_INPUT_IDS[field] ?? null);
    setActiveTab(tabOfField(field));
  }

  useEffect(() => {
    if (!pendingFocusId) return;
    document.getElementById(pendingFocusId)?.focus();
    setPendingFocusId(null);
  }, [pendingFocusId, activeTab]);


  // Photo upload
  const { state: uploadState, upload, reset: resetUpload } = usePhotoUpload();
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const localPreviewUrlRef = useRef<string | null>(null);

  const dropzonePreviewUrl =
    uploadState.status === 'success'
      ? uploadState.previewUrl
      : (localPreviewUrl ?? aquarium?.imageUrl ?? null);

  function handleFileSelect(file: File): void {
    if (isEditMode && aquarium) {
      void upload(file, aquarium.id);
    } else {
      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
      }
      const url = URL.createObjectURL(file);
      localPreviewUrlRef.current = url;
      setLocalPreviewUrl(url);
      setPendingFile(file);
    }
  }

  useEffect(() => {
    if (isOpen) {
      if (aquarium) {
        setName(aquarium.name);
        setDescription(aquarium.description ?? '');
        setTankType(aquarium.tankType ?? '');
        setSetupDate((aquarium.setupDate ?? '').slice(0, 10));
        setGrossVolume(aquarium.grossVolume != null ? String(aquarium.grossVolume) : '');
        setNetVolume(aquarium.netVolume != null ? String(aquarium.netVolume) : '');
        setLength(aquarium.length != null ? String(aquarium.length) : '');
        setWidth(aquarium.width != null ? String(aquarium.width) : '');
        setHeight(aquarium.height != null ? String(aquarium.height) : '');
        setSumpAvailable(aquarium.sumpAvailable ?? false);
        setSupplySystem(aquarium.supplySystem ?? '');
        // Build entries — merge saved data into the canonical EQUIPMENT_TYPES list
        setEquipment(
          EQUIPMENT_TYPES.map((type) => {
            const saved = (aquarium.equipment ?? []).find((e) => e.type === type);
            if (saved && saved.units.length > 0) {
              return { type, enabled: true, units: saved.units.map((u) => ({ brand: u.brand, model: u.model, installedAt: u.installedAt ?? '' })) };
            }
            return { type, enabled: false, units: [{ ...DEFAULT_UNIT }] };
          })
        );
        setEquipmentNotes(aquarium.equipmentNotes ?? '');
        setNotes(aquarium.notes ?? '');
      } else {
        setName('');
        setDescription('');
        setTankType('');
        setSetupDate('');
        setGrossVolume('');
        setNetVolume('');
        setLength('');
        setWidth('');
        setHeight('');
        setSumpAvailable(false);
        setSupplySystem('');
        setEquipment(makeDefaultEquipment());
        setEquipmentNotes('');
        setNotes('');
      }
      setTouched(new Set());
      setSubmitAttempted(false);
      setActiveTab('basic');
      resetUpload();
      setPendingFile(null);
      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
        localPreviewUrlRef.current = null;
      }
      setLocalPreviewUrl(null);
    }
  }, [isOpen, aquarium]); // eslint-disable-line react-hooks/exhaustive-deps

  // A new submit error: show it again, and jump to the field the backend rejected.
  // (Declared after the open/reset effect so it wins over its tab reset.)
  useEffect(() => {
    setSubmitErrorDismissed(false);
    const info = classifySubmitError(error);
    if (info?.kind === 'validation' && info.field) {
      setActiveTab(tabOfField(info.field));
    }
  }, [error, setActiveTab]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (isLoading || isSubmitting) return;
    if (!isValid) {
      // Reveal every error and jump to the first problem (possibly on another tab).
      setSubmitAttempted(true);
      goToField(problemFields[0]);
      return;
    }

    const payload: AquariumFormData = { name: trimmedName };

    const trimmedDescription = description.trim();
    if (trimmedDescription) payload.description = trimmedDescription;

    const trimmedTankType = tankType.trim();
    if (trimmedTankType) payload.tankType = trimmedTankType;

    if (setupDate) payload.setupDate = setupDate;

    for (const field of NUMERIC_FIELDS) {
      const raw = numericValues[field];
      if (raw !== '') {
        payload[field] = Number(raw);
      } else if (isEditMode) {
        // Edit: an emptied field clears the stored value. Create: just omit it.
        payload[field] = null;
      }
    }
    payload.sumpAvailable = sumpAvailable;

    if (supplySystem) payload.supplySystem = supplySystem;
    payload.equipment = equipment
      .filter((e) => e.enabled)
      .map(({ type, units }) => ({
        type,
        units: units.map((u) => ({ brand: u.brand.trim(), model: u.model.trim(), installedAt: u.installedAt || null })),
      }));

    const trimmedEquipmentNotes = equipmentNotes.trim();
    if (trimmedEquipmentNotes) payload.equipmentNotes = trimmedEquipmentNotes;

    const trimmedNotes = notes.trim();
    if (trimmedNotes) payload.notes = trimmedNotes;

    setIsSubmitting(true);
    try {
      const newId = await onSubmit(payload);
      if (pendingFile && newId) {
        await upload(pendingFile, newId);
      }
    } catch {
      // The host page surfaces the failure through the `error` prop (translated);
      // swallow here so a rejected submit is not an unhandled promise rejection.
    } finally {
      setIsSubmitting(false);
    }
  }

  // Equipment helpers
  function toggleEquipment(type: string, checked: boolean): void {
    setEquipment((prev) =>
      prev.map((e) =>
        e.type === type
          ? { ...e, enabled: checked, units: e.units.length > 0 ? e.units : [{ ...DEFAULT_UNIT }] }
          : e
      )
    );
  }

  function addUnit(type: string): void {
    setEquipment((prev) =>
      prev.map((e) => e.type === type ? { ...e, units: [...e.units, { ...DEFAULT_UNIT }] } : e)
    );
  }

  function removeUnit(type: string, idx: number): void {
    setEquipment((prev) =>
      prev.map((e) => {
        if (e.type !== type) return e;
        const next = e.units.filter((_, i) => i !== idx);
        return { ...e, units: next.length > 0 ? next : [{ ...DEFAULT_UNIT }] };
      })
    );
  }

  function updateUnit(type: string, idx: number, field: 'brand' | 'model' | 'installedAt', value: string): void {
    setEquipment((prev) =>
      prev.map((e) => {
        if (e.type !== type) return e;
        const units = e.units.map((u, i) => i === idx ? { ...u, [field]: value } : u);
        return { ...e, units };
      })
    );
  }

  const isBusy = isLoading || isSubmitting;

  function handleClose(): void {
    if (!isBusy) onClose();
  }

  function handleOverlayClick(e: MouseEvent<HTMLDivElement>): void {
    if (e.target === e.currentTarget) handleClose();
  }

  // Escape is the keyboard equivalent of the backdrop click.
  useEffect(() => {
    if (!isOpen) return undefined;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !isBusy) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, isBusy, onClose]);

  if (!isOpen) return null;

  return (
    // The backdrop click is a pointer convenience; keyboard users close with Escape.
    <div className="aq-form__overlay" role="presentation" onClick={handleOverlayClick}>
      <div
        className="aq-form__panel"
        role="dialog"
        aria-modal="true"
        aria-label={isEditMode ? t('aquarium.form.edit.title') : t('aquarium.form.create.title')}
      >

        {/* Header */}
        <div className="aq-form__header">
          <Heading level={2} className="aq-form__title">
            {isEditMode ? t('aquarium.form.edit.title') : t('aquarium.form.create.title')}
          </Heading>
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={isBusy}
            aria-label={t('common.close')}
            className="aq-form__close-btn"
          >
            ×
          </Button>
        </div>

        <form
          aria-label={isEditMode ? t('aquarium.form.edit.title') : t('aquarium.form.create.title')}
          onSubmit={handleSubmit}
          onChange={() => { setSubmitErrorDismissed(true); }}
          noValidate
          className="aq-form__form"
        >
          {/* Tab bar — pinned, never scrolls */}
          <div className="aq-form__tabwrap">
            <AquariumFormTabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
              errorTabs={errorTabs}
            />
          </div>

          {/* Scrollable content */}
          <div className="aq-form__body">

          {/* ── Basic tab ── */}
          <AquariumFormTabPanel id="basic" activeTab={activeTab}>
            {/* Photo */}
            <div className="aq-form__field">
              <Text size="sm" weight="medium" className="aq-form__label">{t('aquarium.form.photo.label')}</Text>
              <PhotoUploadDropzone
                onChange={handleFileSelect}
                previewUrl={dropzonePreviewUrl ?? undefined}
                disabled={isBusy}
                ariaLabel={t('aquarium.form.photo.dropzoneLabel')}
              />
              {(uploadState.status === 'compressing') && (
                <Text size="sm" color="secondary" className="aq-form__upload-status">{t('aquarium.form.photo.compressing')}</Text>
              )}
              {(uploadState.status === 'uploading') && (
                <Text size="sm" color="secondary" className="aq-form__upload-status">
                  {t('aquarium.form.photo.uploading').replace('{progress}', String(uploadState.progress))}
                </Text>
              )}
              {(uploadState.status === 'success') && (
                <Text size="sm" color="success" className="aq-form__upload-status aq-form__upload-status--success">
                  {t('aquarium.form.photo.success')}
                </Text>
              )}
              {(uploadState.status === 'error') && (
                <FieldError id="aq-photo-error" className="aq-form__upload-status">
                  {t('aquarium.form.photo.error')}
                </FieldError>
              )}
            </div>

            <Text size="sm" color="secondary" className="aq-form__required-legend">
              {t('aquarium.form.requiredLegend')}
            </Text>

            {/* Name */}
            <div className="aq-form__field">
              <Input
                id="aq-name"
                label={t('aquarium.form.name.label')}
                required
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); }}
                onBlur={() => { markTouched('name'); }}
                disabled={isBusy}
                maxLength={100}
                inputClassName={`aq-form__input${fieldError('name') ? ' aq-form__input--error' : ''}`}
                error={fieldError('name')}
              />
            </div>

            {/* Description */}
            <div className="aq-form__field">
              <Textarea
                id="aq-description"
                label={t('aquarium.form.description.label')}
                value={description}
                onChange={(e) => { setDescription(e.target.value); }}
                disabled={isBusy}
                rows={3}
                error={fieldError('description')}
              />
            </div>

            {/* Tank Type */}
            <div className="aq-form__field">
              <Input
                id="aq-tank-type"
                label={t('aquarium.form.tankType.label')}
                type="text"
                value={tankType}
                onChange={(e) => { setTankType(e.target.value); }}
                disabled={isBusy}
                list="aq-tank-type-suggestions"
                placeholder={t('aquarium.form.tankType.placeholder')}
                inputClassName="aq-form__input"
                error={fieldError('tankType')}
              />
              <datalist id="aq-tank-type-suggestions">
                {TANK_TYPE_SUGGESTIONS.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </div>

            {/* Setup Date */}
            <div className="aq-form__field">
              <Input
                id="aq-setup-date"
                label={t('aquarium.form.setupDate')}
                type="date"
                value={setupDate}
                onChange={(e) => { setSetupDate(e.target.value); }}
                disabled={isBusy}
                inputClassName="aq-form__input"
                error={fieldError('setupDate')}
              />
            </div>
          </AquariumFormTabPanel>

          {/* ── Dimensions tab ── */}
          <AquariumFormTabPanel id="dimensions" activeTab={activeTab}>
            {/* Volume */}
            <div className="aq-form__field--grid-2">
              {([
                { field: 'grossVolume', id: 'aq-gross-volume', value: grossVolume, set: setGrossVolume },
                { field: 'netVolume',   id: 'aq-net-volume',   value: netVolume,   set: setNetVolume   },
              ] as const).map(({ field, id, value, set }) => (
                <div key={id}>
                  <Input
                    id={id}
                    label={fieldLabel(field)}
                    type="number"
                    value={value}
                    onChange={(e) => { set(e.target.value); }}
                    onBlur={() => { markTouched(field); }}
                    disabled={isBusy}
                    min="0"
                    step="any"
                    inputClassName="aq-form__input"
                    error={fieldError(field)}
                  />
                </div>
              ))}
            </div>

            {/* Dimensions */}
            <div className="aq-form__field--grid-3">
              {([
                { field: 'length', id: 'aq-length', value: length, set: setLength },
                { field: 'width',  id: 'aq-width',  value: width,  set: setWidth  },
                { field: 'height', id: 'aq-height', value: height, set: setHeight },
              ] as const).map(({ field, id, value, set }) => (
                <div key={id}>
                  <Input
                    id={id}
                    label={fieldLabel(field)}
                    type="number"
                    value={value}
                    onChange={(e) => { set(e.target.value); }}
                    onBlur={() => { markTouched(field); }}
                    disabled={isBusy}
                    min="0"
                    step="any"
                    inputClassName="aq-form__input"
                    error={fieldError(field)}
                  />
                </div>
              ))}
            </div>

            {/* Has Sump */}
            <div className="aq-form__field">
              <fieldset className="aq-form__fieldset">
                <legend className="aq-form__legend">
                  {t('aquarium.form.hasSump')}
                </legend>
                <div className="aq-form__radio-group">
                  {[
                    { value: 'yes', label: t('common.yes'), checked: sumpAvailable,  onChange: () => { setSumpAvailable(true); } },
                    { value: 'no',  label: t('common.no'),  checked: !sumpAvailable, onChange: () => { setSumpAvailable(false); } },
                  ].map(({ value, label, checked, onChange }) => (
                    <Input
                      key={value}
                      id={`aq-has-sump-${value}`}
                      type="radio"
                      name="hasSump"
                      label={label}
                      value={value}
                      checked={checked}
                      onChange={onChange}
                      disabled={isBusy}
                      className="aq-form__radio-label"
                      inputClassName="aq-form__choice"
                    />
                  ))}
                </div>
              </fieldset>
            </div>

            {/* Supply System */}
            <div className="aq-form__field">
              <Select
                id="aq-supply-system"
                label={t('aquarium.form.supplySystem.label')}
                value={supplySystem}
                onChange={(e) => { setSupplySystem(e.target.value); }}
                disabled={isBusy}
                className="aq-form__input aq-form__select"
              >
                <option value="">{t('aquarium.form.supplySystem.placeholder')}</option>
                {SUPPLY_SYSTEMS.map((value) => (
                  <option key={value} value={value}>{t(`aquarium.form.supplySystem.options.${value}`)}</option>
                ))}
              </Select>
            </div>
          </AquariumFormTabPanel>

          {/* ── Equipment tab ── */}
          <AquariumFormTabPanel id="equipment" activeTab={activeTab}>
            {/* Technical Equipment */}
            <div className="aq-form__field">
              <Text size="sm" weight="medium" className="aq-form__label">{t('aquarium.form.equipment.label')}</Text>
              <div className="aq-form__equipment-grid">
                {equipment.map((entry) => {
                  const typeLabel = t(`aquarium.form.equipmentTypes.${entry.type}`);
                  return (
                    <div key={entry.type} className={`aq-form__equipment-block${entry.enabled ? ' aq-form__equipment-block--open' : ''}`}>
                      {/* Checkbox row */}
                      <div className="aq-form__equipment-toggle">
                        <Input
                          id={`aq-equipment-${entry.type}`}
                          type="checkbox"
                          label={typeLabel}
                          checked={entry.enabled}
                          disabled={isBusy}
                          onChange={(e) => { toggleEquipment(entry.type, e.target.checked); }}
                          className="aq-form__equipment-check"
                          inputClassName="aq-form__choice"
                        />
                        {entry.enabled && (
                          <span className="aq-form__equipment-count">
                            {entry.units.length} {entry.units.length === 1
                              ? t('aquarium.form.equipment.unit')
                              : t('aquarium.form.equipment.units')}
                          </span>
                        )}
                      </div>

                      {/* Units */}
                      {entry.enabled && (
                        <div className="aq-form__equipment-units">
                          {entry.units.map((unit, idx) => (
                            <Fragment key={idx}>
                              <div className="aq-form__equipment-unit-row">
                                <span className="aq-form__equipment-unit-index">{idx + 1}</span>
                                <Input
                                  type="text"
                                  value={unit.brand}
                                  aria-label={`${typeLabel} ${idx + 1} – ${t('aquarium.form.equipment.brand')}`}
                                  placeholder={t('aquarium.form.equipment.brand')}
                                  disabled={isBusy}
                                  inputClassName="aq-form__input"
                                  onChange={(e) => { updateUnit(entry.type, idx, 'brand', e.target.value); }}
                                />
                                <Input
                                  type="text"
                                  value={unit.model}
                                  aria-label={`${typeLabel} ${idx + 1} – ${t('aquarium.form.equipment.model')}`}
                                  placeholder={t('aquarium.form.equipment.model')}
                                  disabled={isBusy}
                                  inputClassName="aq-form__input"
                                  onChange={(e) => { updateUnit(entry.type, idx, 'model', e.target.value); }}
                                />
                                {entry.units.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    className="aq-form__equipment-remove"
                                    aria-label={t('aquarium.form.equipment.removeUnit', { index: idx + 1 })}
                                    disabled={isBusy}
                                    onClick={() => { removeUnit(entry.type, idx); }}
                                  >
                                    ×
                                  </Button>
                                )}
                              </div>
                              <div className="aq-form__equipment-unit-date">
                                <Input
                                  id={`equipment-${entry.type}-${idx}-installed`}
                                  type="date"
                                  size="sm"
                                  value={unit.installedAt}
                                  max={todayIso}
                                  inputClassName="aq-form__input"
                                  label={t('aquarium.form.equipment.installedAt', { unit: `${typeLabel} ${idx + 1}` })}
                                  disabled={isBusy}
                                  onChange={(e) => { updateUnit(entry.type, idx, 'installedAt', e.target.value); }}
                                />
                              </div>
                            </Fragment>
                          ))}
                          <Button
                            type="button"
                            variant="secondary"
                            className="aq-form__equipment-add"
                            disabled={isBusy}
                            onClick={() => { addUnit(entry.type); }}
                          >
                            + {t('aquarium.form.equipment.addUnit')}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Equipment Notes */}
            <div className="aq-form__field">
              <Textarea
                id="aq-equipment-notes"
                label={t('aquarium.form.equipmentNotes.label')}
                value={equipmentNotes}
                onChange={(e) => { setEquipmentNotes(e.target.value); }}
                disabled={isBusy}
                rows={3}
              />
            </div>
          </AquariumFormTabPanel>

          {/* ── Notes tab ── */}
          <AquariumFormTabPanel id="notes" activeTab={activeTab}>
            <div className="aq-form__field">
              <Textarea
                id="aq-notes"
                label={t('aquarium.detail.notes')}
                value={notes}
                onChange={(e) => { setNotes(e.target.value); }}
                disabled={isBusy}
                rows={5}
              />
            </div>
          </AquariumFormTabPanel>

          {/* Submit-level error */}
          {bannerMessage != null && (
            <Alert className="aq-form__submit-alert">{bannerMessage}</Alert>
          )}
          </div>{/* end .aq-form__body */}

          {/* Actions — pinned at bottom */}
          <div className="aq-form__actions">
            {!isValid && !isBusy && (
              <div className="aq-form__submit-hint">
                <Text id="aq-form-submit-hint" size="sm" color="secondary" aria-live="polite">
                  {missingRequired.length > 0 && t('aquarium.form.requiredHint', {
                    fields: missingRequired.map(fieldLabel).join(', '),
                  })}
                  {missingRequired.length > 0 && invalidFields.length > 0 && ' '}
                  {invalidFields.length > 0 && t('aquarium.form.invalidHint', {
                    fields: invalidFields.map(fieldLabel).join(', '),
                  })}
                </Text>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="aq-form__submit-hint-action"
                  onClick={() => { setSubmitAttempted(true); goToField(problemFields[0]); }}
                >
                  {t('aquarium.form.goToField')}
                </Button>
              </div>
            )}
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isBusy}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isBusy || !isValid}
              aria-describedby={!isValid && !isBusy ? 'aq-form-submit-hint' : undefined}
            >
              {isBusy
                ? t('common.loading')
                : isEditMode
                  ? t('aquarium.form.edit.submit')
                  : t('aquarium.form.create.submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
